import React, { useEffect, useRef, useState } from "react";
import { ssoLogin } from "../../services/login";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import Template from "../../components/SignInPageComponents/Template";
import LoadingAnimation from "../../assets/LoadingAnimation.svg";
import { FaShopify } from "react-icons/fa";
import { HiOutlineClock } from "react-icons/hi";
import { getStoredValue, safeSessionStorage } from "../../services/logics";
import { getSSOConfig } from "../../utils/ssoProvider";

const Signin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const ssoInitialized = useRef(false);
  const spCallbackHandled = useRef(false);
  const [loading, setLoading] = useState(false);
  const [ssoButtonReady, setSsoButtonReady] = useState(false);

  // Check if user is already logged in
  const token = getStoredValue("token");
  const isLoggedIn = getStoredValue("isLoggedIn") === "true";

  // ✅ Detect OAuth pending parameters from URL
  const urlParams = new URLSearchParams(location.search);
  const storedParams = safeSessionStorage.getItem("oauth_params_on_load");
  const storedUrlParams = storedParams ? new URLSearchParams(storedParams) : null;

  const getUrlParam = key => urlParams.get(key) || storedUrlParams?.get(key);

  // Use current URL params, or fall back to stored params if not in current URL
  const oauthPending = urlParams.get("oauth_pending") === "true" || storedUrlParams?.get("oauth_pending") === "true";
  const shop = getUrlParam("shop");
  const code = getUrlParam("code");
  const app_type = getUrlParam("app_type") || "public";
  const state = getUrlParam("state");
  const sessionExpired = urlParams.get("session_expired") === "true";

  useEffect(() => {
    // Which SSO widget loads is decided purely by the domain the app is running
    // on - see domainConfig.json / ssoProvider.js. The two widget scripts define
    // the exact same globals, so only one of them can ever be on the page at once.
    const scriptUrl = getSSOConfig().scriptUrl;

    const setupSSO = () => {
      window._callbackSPLogin = response => {
        if (response?.data?.token && response.data._callback) {
          window.location.href = `${response.data._callback}?token=${response.data.token}`;
        }
      };

      const tryRenderButton = () => {
        const container = document.querySelector(".sp_login_btn");
        if (container && !ssoInitialized.current) {
          container.innerHTML = "";

          if (typeof window.createSPSignInButton === "function") {
            // Patch the original function to prevent double render
            const originalFn = window.createSPSignInButton;
            window.createSPSignInButton = () => {
              const innerContainer = document.querySelector(".sp_login_btn");
              if (innerContainer && innerContainer.children.length === 0) {
                originalFn(); // run only if no buttons yet
              }
            };
            window.createSPSignInButton();
            ssoInitialized.current = true;
            setSsoButtonReady(true);
            return true;
          }
        }
        return false;
      };

      // Retry logic in case of incognito or delay in availability
      let attempts = 0;
      const maxAttempts = 10;
      const interval = setInterval(() => {
        if (tryRenderButton() || attempts >= maxAttempts) {
          clearInterval(interval);
          // maxAttempts (10s) reached with no button: stop showing the loader either way,
          // so the page doesn't spin forever if the widget script never calls back.
          if (attempts >= maxAttempts) setSsoButtonReady(true);
        }
        attempts++;
      }, 1000);
    };

    // Load the script if not already present
    if (!document.querySelector(`script[src="${scriptUrl}"]`)) {
      const script = document.createElement("script");
      script.src = scriptUrl;
      script.async = true;
      script.onload = setupSSO;
      document.body.appendChild(script);
    } else {
      setupSSO();
    }

    return () => {
      window._callbackSPLogin = null;
    };
  }, []);

  // Function to handle the SP_callback
  const handleSPCallback = async token => {
    if (token) {
      setLoading(true);
      try {
        // Same widget callback fires for every provider; ssoLogin looks up the
        // right backend endpoint for the current domain via domainConfig.json.
        await ssoLogin(token);

        // ✅ If OAuth is pending, redirect to integration page to complete it
        if (oauthPending && code && shop) {
          toast.success("Login successful! Completing Shopify connection...");

          // Build OAuth completion URL with all params
          const oauthUrl = `/integration-page?code=${code}&shop=${shop}&app_type=${app_type}&authenticated=true${state ? `&state=${state}` : ""}`;
          navigate(oauthUrl, { replace: true });
        } else {
          // replace: true so `/signin?token=...` never re-enters history - browser
          // back/forward landing back on it would re-run the SP callback below
          // with the same token, re-logging in mid-render and racing PrivateLayout's
          // auth check into a spurious "session expired" flash.
          navigate("/", { replace: true });
        }
      } catch (error) {
        toast.error(`${error.response?.data?.detail || error.response?.data?.error || "An unexpected error occurred"}`);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    if (token && !spCallbackHandled.current) {
      spCallbackHandled.current = true;
      // Strip ?token=... from the URL immediately, synchronously, before the
      // async ssoLogin below resolves. This must survive a remount (browser
      // back/forward onto /signin?token=..., or any re-render of this route
      // before navigate("/") lands) - a per-instance ref alone resets on
      // remount and re-submits the same already-consumed SP token, which
      // re-writes auth storage mid-render and races PrivateLayout's auth
      // check into a spurious "session expired" flash.
      urlParams.delete("token");
      const strippedSearch = urlParams.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${strippedSearch ? `?${strippedSearch}` : ""}`);
      handleSPCallback(token);
    }
  }, [navigate]);

  useEffect(() => {
    if (!sessionExpired) return;
    // Strip ?session_expired=true so a page refresh here doesn't re-show the banner
    // (sessionExpired itself is read once from location.search at mount, so the
    // banner still renders for the rest of this page's lifetime).
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete("session_expired");
    const strippedSearch = urlParams.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${strippedSearch ? `?${strippedSearch}` : ""}`);
  }, [sessionExpired]);

  return (
    <Template
      title="Login to your account"
      description="Or login with"
      formType="signin"
      className="flex justify-center"
      beforeTitle={
        sessionExpired && (
          <div
            data-testid="session-expired-banner"
            className="flex items-center gap-3 p-4 w-full rounded-lg border border-[#48BB7833]"
            style={{
              background: "linear-gradient(135deg, rgba(72,187,120,0.10) 0%, rgba(72,187,120,0.03) 100%)",
            }}
          >
            <span className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-[#48BB7826]">
              <HiOutlineClock className="text-[#48BB78] text-xl" />
            </span>
            <div className="text-left">
              <p className="text-[#FFFFFF] text-sm font-semibold leading-tight">Your session has expired</p>
              <p className="text-[#ABABAB] text-xs mt-0.5">Sign in again to pick up right where you left off.</p>
            </div>
          </div>
        )
      }
    >
      {/* Centered Login Button */}
      <div className="relative z-10 flex flex-col justify-center items-center h-full gap-6">
        {loading ? (
          <img src={LoadingAnimation} alt="Loading..." className="h-full w-12 lg:w-20" />
        ) : (
          <>
            {/* ✅ Show OAuth pending notice ONLY if user is NOT logged in and Shopify connection is waiting */}
            {oauthPending && shop && !token && !isLoggedIn && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-md w-full">
                <div className="flex items-start">
                  <FaShopify className="text-blue-500 text-2xl mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-1">Shopify Connection Pending</h3>
                    <p className="text-sm text-blue-700">
                      You're connecting <strong>{shop.replace(".myshopify.com", "")}</strong> to your account. Please
                      sign in to complete the setup.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* The SP widget renders its own black pill button with inline styles (color,
                radius, shadow) that we cannot override from here - this wrapper only centers
                it and reserves its footprint, it does not add any competing border/background. */}
            <div className="relative w-full min-h-[52px] flex items-center justify-center">
              <div className="sp_login_btn" data-testid="sp_login_btn"></div>
              {!ssoButtonReady && (
                <img
                  src={LoadingAnimation}
                  alt="Loading..."
                  data-testid="sso-button-loader"
                  className="absolute w-8 h-8"
                />
              )}
            </div>
          </>
        )}
      </div>
    </Template>
  );
};

export default Signin;
